import { useState } from 'react';
import { allRoles, roleDescriptions, type Role } from '@flintmint/core-shared/constants';
import { Button } from '@flintmint/web-shared/components/button';
import { Chip } from '@flintmint/web-shared/components/chip';
import { Modal } from '@flintmint/web-shared/components/modal';
import { Popover } from '@flintmint/web-shared/components/popover';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';

import { setUserRoles } from '@/services/user-roles-service';
import type { UserRow } from './use-users';

import styles from './roles-cell.module.scss';

export const roleChipColor = (role: Role): 'hero' | 'secondary' => (role === 'admin' ? 'hero' : 'secondary');

export const RolesCell = ({ user, roles }: { user: UserRow; roles: Role[] }) => {
  const [pendingChange, setPendingChange] = useState<{ role: Role; action: 'add' | 'remove' } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { showToastNotification } = useToaster();
  // Own admin chip is not removable: an admin cannot remove their own admin role.
  const isSelf = user.userId === getAuthUser().id;

  const applyChange = async (role: Role, action: 'add' | 'remove'): Promise<boolean> => {
    const nextRoles = action === 'add' ? (roles.includes(role) ? roles : [...roles, role]) : roles.filter((r) => r !== role);
    setIsSaving(true);
    try {
      await setUserRoles(user.userId, nextRoles);
      return true;
    } catch {
      showToastNotification({
        type: 'error',
        title: 'Failed to update roles',
        description: user.email,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const addableRoles = allRoles.filter((role) => !roles.includes(role));

  return (
    <div className={styles.cell}>
      {roles.map((role) => (
        <Chip
          key={role}
          label={role}
          color={roleChipColor(role)}
          onClose={isSaving || (isSelf && role === 'admin') ? undefined : () => setPendingChange({ role, action: 'remove' })}
        />
      ))}
      {addableRoles.length > 0 && (
        <Popover
          placement="bottom-start"
          content={({ close }) => (
            <ul className={styles.addMenu}>
              {addableRoles.map((role) => (
                <li key={role}>
                  <button
                    type="button"
                    className={styles.addMenuItem}
                    disabled={isSaving}
                    onClick={() => {
                      close();
                      setPendingChange({ role, action: 'add' });
                    }}
                  >
                    <span className={styles.addMenuRole}>{role}</span>
                    <span className={styles.addMenuDescription}>{roleDescriptions[role]}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        >
          <Button size="small" appearance="outline" color="light" iconOnly disabled={isSaving} aria-label="Add role">
            +
          </Button>
        </Popover>
      )}
      {pendingChange && (
        <Modal
          open
          onClose={() => !isSaving && setPendingChange(null)}
          title={`${pendingChange.action === 'add' ? 'Grant' : 'Remove'} ${pendingChange.role}`}
          primaryButton={{
            children: 'Confirm',
            disabled: isSaving,
            onClick: async () => {
              const succeeded = await applyChange(pendingChange.role, pendingChange.action);
              if (succeeded) setPendingChange(null);
            },
          }}
          secondaryButton={{ children: 'Cancel', onClick: () => setPendingChange(null), disabled: isSaving }}
        >
          <p>
            {pendingChange.action === 'add' ? 'Grant' : 'Remove'} the {pendingChange.role} role{' '}
            {pendingChange.action === 'add' ? 'to' : 'from'} {user.email}?
          </p>
          <p>
            {pendingChange.action === 'add' ? 'Grants' : 'Revokes'}: {roleDescriptions[pendingChange.role]}
          </p>
        </Modal>
      )}
    </div>
  );
};
